import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Avatar, IconButton, Tooltip } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const accent = '#7C3AED';
const ownBubble = { background: 'rgba(107, 61, 255, 0.59)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', color: '#fff', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.3)' };
const otherBubble = { background: 'rgba(180, 200, 230, 0.45)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', border: '1px solid rgba(180,200,230,0.4)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 172800000) return 'Ayer ' + d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
};

const isImage = (mime) => mime && mime.startsWith('image/');
const isAudio = (mime) => mime && mime.startsWith('audio/');

const NUM_BARS = 40;
const BAR_WIDTH = 3;
const BAR_GAP = 1.5;
const BAR_MIN = 3;
const BAR_MAX = 28;

let _audioCtx = null;
const getAudioCtx = () => {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return _audioCtx;
};

const extractWaveform = async (url) => {
  const resp = await fetch(url);
  const buf = await resp.arrayBuffer();
  const audioCtx = getAudioCtx();
  const decoded = await audioCtx.decodeAudioData(buf);
  const raw = decoded.getChannelData(0);
  const step = Math.floor(raw.length / NUM_BARS);
  const bars = [];
  for (let i = 0; i < NUM_BARS; i++) {
    let sum = 0;
    for (let j = 0; j < step; j++) sum += Math.abs(raw[i * step + j]);
    bars.push(sum / step);
  }
  const max = Math.max(...bars, 0.001);
  return { bars: bars.map(b => BAR_MIN + (b / max) * (BAR_MAX - BAR_MIN)), duration: decoded.duration };
};

const fmtDur = (s) => {
  if (!s || !isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const AudioWaveformPlayer = ({ url, isOwn }) => {
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const [bars, setBars] = useState(null);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    let cancelled = false;
    extractWaveform(url).then(({ bars: b, duration: d }) => {
      if (!cancelled) { setBars(b); setDuration(d); }
    }).catch(() => {
      if (!cancelled) setBars(Array(NUM_BARS).fill(BAR_MIN + (BAR_MAX - BAR_MIN) * 0.3));
    });
    return () => { cancelled = true; };
  }, [url]);

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (a && !a.paused && a.duration) {
      setProgress(a.currentTime / a.duration);
      setCurrentTime(a.currentTime);
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
      rafRef.current = requestAnimationFrame(tick);
    } else {
      a.pause();
      setPlaying(false);
      cancelAnimationFrame(rafRef.current);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    cancelAnimationFrame(rafRef.current);
  };

  const handleBarClick = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    a.currentTime = pct * a.duration;
    setProgress(pct);
    setCurrentTime(a.currentTime);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const playedColor = isOwn ? '#fff' : accent;
  const unplayedColor = isOwn ? 'rgba(255,255,255,0.35)' : 'rgba(124,58,237,0.25)';
  const timeColor = isOwn ? 'rgba(255,255,255,0.8)' : '#6b7280';

  return (
    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, minWidth: 220, maxWidth: 300 }}>
      <audio ref={audioRef} src={url} preload="metadata" onEnded={handleEnded} />
      <IconButton onClick={togglePlay} size="small" sx={{ color: playedColor, p: 0.5 }}>
        {playing ? <PauseIcon sx={{ fontSize: 22 }} /> : <PlayArrowIcon sx={{ fontSize: 22 }} />}
      </IconButton>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
        <Box
          onClick={handleBarClick}
          sx={{
            display: 'flex', alignItems: 'flex-end', gap: `${BAR_GAP}px`,
            height: BAR_MAX, cursor: 'pointer', userSelect: 'none',
          }}
        >
          {(bars || Array(NUM_BARS).fill(BAR_MIN)).map((h, i) => {
            const pct = i / NUM_BARS;
            return (
              <Box
                key={i}
                sx={{
                  width: BAR_WIDTH,
                  height: h,
                  borderRadius: 1,
                  background: pct < progress ? playedColor : unplayedColor,
                  transition: 'background 0.1s',
                  flexShrink: 0,
                }}
              />
            );
          })}
        </Box>
        <Typography variant="caption" sx={{ fontSize: 10, color: timeColor, lineHeight: 1 }}>
          {playing || currentTime > 0 ? fmtDur(currentTime) : fmtDur(duration)}
        </Typography>
      </Box>
    </Box>
  );
};

const FileAttachment = ({ att, isOwn, onImageClick }) => {
  const baseURL = window.location.origin;
  const url = att.url?.startsWith('http') ? att.url : `${baseURL}${att.url}`;

  if (isImage(att.mimetype)) {
    return (
      <Box
        onClick={() => onImageClick && onImageClick(url, att.originalName || att.filename)}
        sx={{ display: 'block', mt: 0.5, maxWidth: 260, borderRadius: 1, overflow: 'hidden', cursor: 'pointer' }}
      >
        <Box component="img" src={url} alt={att.originalName} sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1 }} />
      </Box>
    );
  }

  if (isAudio(att.mimetype)) {
    return <AudioWaveformPlayer url={url} isOwn={isOwn} />;
  }

  return (
    <Box
      component="a"
      href={url}
      download={att.originalName || att.filename}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, p: 1,
        background: isOwn ? 'rgba(255,255,255,0.15)' : '#f3f4f6', borderRadius: 1,
        textDecoration: 'none', color: 'inherit',
        '&:hover': { background: isOwn ? 'rgba(255,255,255,0.25)' : '#e5e7eb' }
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 20, color: isOwn ? 'rgba(255,255,255,0.8)' : '#6b7280' }} />
      <Typography variant="caption" sx={{ color: isOwn ? 'rgba(255,255,255,0.9)' : '#374151', wordBreak: 'break-all' }}>
        {att.originalName || att.filename}
      </Typography>
    </Box>
  );
};

const ChatMessageItem = ({ message, isOwn, showAvatar = true, onReply, onImageClick, compact = false }) => {
  if (message.message_type === 'system') {
    return (
      <Box sx={{ py: 0.5, px: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
          {message.content}
        </Typography>
      </Box>
    );
  }

  const bubbleStyle = isOwn ? ownBubble : otherBubble;
  const attachments = message.attachments || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 1,
        px: compact ? 1 : 2,
        py: 0.3,
        '&:hover .reply-btn': { opacity: 1 },
      }}
    >
      {showAvatar && !isOwn && !compact && (
        <Avatar
          src={message.sender_avatar || undefined}
          sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'rgba(124,58,237,0.15)', color: accent }}
        >
          {(message.sender_name || '?')[0]}
        </Avatar>
      )}
      {!showAvatar && !isOwn && !compact && <Box sx={{ width: 28 }} />}

      <Box sx={{ maxWidth: compact ? '85%' : '70%', minWidth: 60 }}>
        {/* Reply quote */}
        {message.reply_to_id && message.reply_content && (
          <Box sx={{
            px: 1.5, py: 0.5, mb: 0.3,
            borderLeft: `3px solid ${accent}`,
            background: 'rgba(124,58,237,0.12)',
            backdropFilter: 'blur(1px)', WebkitBackdropFilter: 'blur(1px)',
            borderRadius: '0 8px 8px 0',
          }}>
            <Typography variant="caption" sx={{ color: '#4a1d96', fontWeight: 700, fontSize: 11 }}>
              {message.reply_sender_name || message.reply_sender_id}
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: '#374151', fontSize: 11 }} noWrap>
              {message.reply_content}
            </Typography>
          </Box>
        )}

        {/* Bubble */}
        <Box sx={{
          ...bubbleStyle,
          borderRadius: isOwn ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          px: 1.5,
          py: 0.8,
          position: 'relative',
        }}>
          {!isOwn && !compact && showAvatar && (
            <Typography variant="caption" sx={{ color: accent, fontWeight: 600, fontSize: 11, display: 'block', mb: 0.2 }}>
              {message.sender_name}
            </Typography>
          )}

          {message.content && (
            <Typography variant="body2" sx={{ color: isOwn ? '#fff' : '#000', fontSize: compact ? 13 : 15, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 700, textShadow: isOwn ? '0 1px 2px rgba(0,0,0,0.3)' : 'none' }}>
              {message.content}
            </Typography>
          )}

          {attachments.map((att, i) => (
            <FileAttachment key={i} att={att} isOwn={isOwn} onImageClick={onImageClick} />
          ))}

          <Typography variant="caption" sx={{
            display: 'block',
            textAlign: 'right',
            color: '#000',
            fontWeight: 400,
            textShadow: 'none',
            fontSize: 10,
            mt: 0.3
          }}>
            {formatTime(message.created_at)}
          </Typography>
        </Box>
      </Box>

      {onReply && (
        <IconButton
          className="reply-btn"
          size="small"
          onClick={() => onReply(message)}
          sx={{ opacity: 0, transition: 'opacity 0.2s', color: '#9ca3af', p: 0.3 }}
        >
          <ReplyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default ChatMessageItem;
